import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CodyIconBadge } from '../components/CodyLogo';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, ArrowRight, Mail, Key, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email | code | done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Reset code sent! Check your email.');
      setStep('code');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || password.length < 6) { toast.error('Enter the 6-digit code and a password (min 6 chars)'); return; }
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, code, new_password: password }) });
      if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Password reset! You can now log in.');
      setStep('done');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="absolute top-0 left-0 right-0 h-[40vh] hero-gradient" />
      <Card className="relative w-full max-w-md p-8 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] border-[#E6E8EE]">
        <div className="flex items-center justify-center gap-2 mb-8">
          <CodyIconBadge size={44} />
          <span className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk'" }}>Cody</span>
        </div>

        {step === 'email' && (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF2EA, #FFE8DE)' }}>
                <Mail className="w-5 h-5 text-[#FF4500]" />
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Forgot password?</h2>
              <p className="text-sm text-[#5B667A]">Enter your email and we'll send a reset code</p>
            </div>
            <form onSubmit={handleSendCode} className="space-y-4">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="h-11 rounded-lg" autoFocus />
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
                {loading ? 'Sending...' : 'Send Reset Code'} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF2EA, #FFE8DE)' }}>
                <Key className="w-5 h-5 text-[#FF4500]" />
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Enter reset code</h2>
              <p className="text-sm text-[#5B667A]">Check <span className="font-semibold text-[#0B0F17]">{email}</span> for a 6-digit code</p>
            </div>
            <form onSubmit={handleReset} className="space-y-4">
              <Input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" className="h-14 text-center text-2xl font-bold tracking-[0.5em] rounded-xl" style={{ fontFamily: "'Space Grotesk'" }} autoFocus />
              <div>
                <label className="text-sm font-medium text-[#0B0F17] mb-1.5 block">New password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="h-11 rounded-lg" />
              </div>
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full h-11 rounded-lg font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
            <button onClick={() => setStep('email')} className="text-xs text-[#5B667A] hover:text-[#0B0F17] mt-4 mx-auto block">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Try a different email
            </button>
          </>
        )}

        {step === 'done' && (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-[#E9F9EF] flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-[#16A34A]" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk'" }}>Password reset!</h2>
            <p className="text-sm text-[#5B667A] mb-6">You can now log in with your new password.</p>
            <Button onClick={() => navigate('/login')} className="h-11 px-8 rounded-lg font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
              Go to Login <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}

        {step !== 'done' && (
          <p className="text-sm text-[#5B667A] text-center mt-6">
            Remember your password? <Link to="/login" className="text-[#FF4500] font-medium hover:underline">Log in</Link>
          </p>
        )}
      </Card>
    </div>
  );
}
